import { useMemo, useContext, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import DeleteIcon from '@mui/icons-material/Delete'
import Button from '@mui/material/Button'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import moment from 'moment'
import { Paper, CvsAvatar, TableCellTooltip } from '@components'
import { apiKeyContext, modalContext } from '@contexts'
import { CvsTable } from '../components/table/cvs-table'

const useStyles = makeStyles(theme => ({
  root: {
    // width: 'calc(100% - 48px)',
    height: '100%',
    marginTop: theme.spacing(3),
  },
  paper: {
    marginTop: theme.spacing(3),
  },
}))

const TypographyPadding = styled(Typography)`
  padding-left: 28px;
  padding-top: 24px;
`

export const ProfileApiKey = () => {
  const classes = useStyles()
  const [clipboardErrors] = useState([])
  const ModalContext = useContext(modalContext)
  const { byId, getPublicApiKeys, createPublicApiKey, revokeApiKey } = useContext(apiKeyContext)
  const memoizedRows = useMemo(() => Object.keys(byId).map(key => byId[key]), [byId])

  useEffect(() => {
    getPublicApiKeys()
  }, [getPublicApiKeys])

  const createPublicKey = () => {
    createPublicApiKey().then
  }

  function copyToClipboard(row) {
    let textToCopy = row.api_key
    if (row.plain_text_secret) {
      textToCopy += ` ${row.plain_text_secret}`
    }

    navigator.clipboard.writeText(textToCopy).catch(() => {
      if (clipboardErrors.length) {
        clipboardErrors.push(
          t`There was an error automatically copying the information for you. Please manual select and copy the information you want to copy.`,
        )
      }
    })
  }

  const deleteApiKey = apiKey => {
    ModalContext.displayModal({
      title: t`Are you sure you want to revoke this API key?`,
      description: t`This action can't be undone.`,
      confirm: () => {
        return () => {
          revokeApiKey(apiKey)
        }
      },
    })
  }

  function getCellContent(type, value) {
    switch (type) {
      case 'avatar':
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <CvsAvatar user={value} />
            {value.name}
          </span>
        )
      case 'text':
        return value
      case 'number':
        return Intl.NumberFormat().format(value)
      case 'date':
        return value ? moment(value).format('MM/DD/YY') : t`Never`
      default:
        return value
    }
  }

  return (
    <motion.div
      className="admin"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.15 } }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
    >
      <Paper elevation={3} className={classes.paper}>
        <Typography variant="h4" style={{ paddingTop: '15px', paddingLeft: '28px' }}>
          {t`API keys`}
        </Typography>
        <div className={classes.root}>
          <Button
            style={{ marginLeft: '28px', color: 'white', height: '35px' }}
            color="secondary"
            variant="contained"
            onClick={createPublicKey}
          >
            {t`Create API key`}
          </Button>
          <TypographyPadding variant="body1">
            {t`Note: API Secret is only available on initial creation`}
          </TypographyPadding>
          <div style={{ maxWidth: '100%', marginTop: '15px' }}>
            <CvsTable
              keyField="id"
              orderBy="last_created"
              order="asc"
              columns={[
                { label: t`API key`, width: '35%', lookup: 'api_key', type: 'text' },
                { label: t`API secret`, width: '35%', lookup: 'api_secret', type: 'text' },
                { label: t`Created`, width: '10%', lookup: 'created_at', type: 'date' },
                { label: t`Expires`, width: '10%', lookup: 'expiration', type: 'date' },
                { label: t`Delete key`, width: '10%' },
              ]}
              rows={memoizedRows}
              noAdd={true}
              noPagination={true}
              style={{ maxWidth: '100%' }}
            >
              {({ row }) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                >
                  <TableCellTooltip title={t`Click to Copy API Key`}>
                    <TableCell
                      component="th"
                      scope="row"
                      padding="normal"
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      onClick={copyToClipboard(row)}
                    >
                      {row.api_key}
                    </TableCell>
                  </TableCellTooltip>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{
                      overflowWrap: 'break-word',
                    }}
                  >
                    {row.plain_text_secret || '•••••••••••••••'}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {getCellContent('date', row.created_at)}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {getCellContent('date', row.expiration)}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="normal"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <DeleteIcon
                      onClick={() => {
                        deleteApiKey(row)
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}
            </CvsTable>
          </div>
        </div>
      </Paper>
    </motion.div>
  )
}
